//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';

import classnames from 'classnames';
import { selectFactory, SelectTypes } from 'common/components/core/select';
import theme from './SingleTeamSelect.scss';
import { memoizedImmutable } from 'common/utils/Memoizer';
import {
    TeamOptionRenderer,
    TeamValueRenderer,
    TeamPlaceholderRenderer
} from './TeamRenderer';
import type { List } from 'immutable';
import type { TFunction } from 'react-i18next';
import { TeamStatusEnum } from 'common/enums/TeamEnum';

const Select = selectFactory({
    Option: TeamOptionRenderer,
    SingleValue: TeamValueRenderer,
    Placeholder: TeamPlaceholderRenderer
});

type Props = {
    theme: Object,
    className?: string | any,
    t: TFunction
};

class SingleTeamSelect extends React.PureComponent<Props> {
    static defaultProps = {
        selectType: SelectTypes.Raised
    };

    @memoizedImmutable()
    getTeamIdsOptions(
        teams: List<Map<string, *>>,
        highlightInactiveTeam: boolean
    ) {
        const options = [];
        if (teams) {
            teams.forEach((team, index) => {
                if (
                    highlightInactiveTeam &&
                    team.status === TeamStatusEnum.Inactive
                ) {
                    return;
                }
                options.push({
                    value: team.teamId,
                    label: team.name || team.teamId,
                    color: team.color,
                    testId: `option-${index + 1}`
                });
            });
        }
        return options;
    }

    render() {
        const {
            theme,
            className,
            teams,
            t,
            selectType,
            testId,
            value,
            highlightInactiveTeam,
            ...others
        } = this.props;
        let { options, showPlaceholderIcon } = this.props;
        let isInactiveTeam = false;
        if (highlightInactiveTeam) {
            const currentTeam = teams.get(value);
            isInactiveTeam =
                currentTeam && currentTeam.status === TeamStatusEnum.Inactive;
        }
        if (teams) {
            options = this.getTeamIdsOptions(teams, highlightInactiveTeam);
        }
        if (selectType === SelectTypes.Raised) {
            showPlaceholderIcon = false;
        }
        return (
            <Select
                showPlaceholderIcon={showPlaceholderIcon}
                className={classnames(theme.singleTeamSelect, className, {
                    [theme.inactiveTeam]: isInactiveTeam
                })}
                theme={theme}
                selectType={selectType}
                options={options}
                openMenuOnFocus
                blurInputOnSelect
                isSearchable
                placeholder={t('selectTeam')}
                testId={testId}
                value={value}
                {...others}
            />
        );
    }
}

export default compose(
    themr('SingleTeamSelect', theme),
    translate()
)(SingleTeamSelect);
